import React from 'react';

import { motion, AnimatePresence } from 'motion/react';

export interface ColorPalette {
  name: string;
  colors: string[];
}

export const colorPalettes: ColorPalette[] = [
  {
    name: "Cornucopia",
    colors: ["#8B4513", "#CD853F", "#DAA520", "#D2691E", "#F4A460", "#FFF8DC"]
  },
  {
    name: "Thanksgiving",
    colors: ["#800000", "#A52A2A", "#D2691E", "#FF8C00", "#FFD700", "#F5DEB3"]
  },
  {
    name: "Bouquet",
    colors: ["#FFB7B2", "#FF9AA2", "#FFDAC1", "#E2F0CB", "#B5EAD7", "#C7CEEA"]
  },
  {
    name: "Cider",
    colors: ["#D89F0E", "#C67C1E", "#A44C16", "#7B3308", "#E8C26C", "#F5E6CC"]
  },
  {
    name: "Autumn",
    colors: ["#5D2906", "#8F3E0A", "#BF5C14", "#D98026", "#F2A745", "#FAE3B2"]
  },
  {
    name: "Ocean Breeze",
    colors: ["#006994", "#0093AF", "#00B9E8", "#74D7EE", "#B2EBF2", "#E0F7FA"]
  },
  {
    name: "Forest Walk",
    colors: ["#1B4D3E", "#2E6B48", "#4A8F5D", "#76B07F", "#A3D1A5", "#D4EBD6"]
  },
  {
    name: "Sunset Vibes",
    colors: ["#4A1C40", "#8C2B59", "#D63E4A", "#F47C43", "#F9A852", "#FCD695"]
  },
  {
    name: "Lavender Dreams",
    colors: ["#4B0082", "#6A0DAD", "#9370DB", "#BA55D3", "#DDA0DD", "#E6E6FA"]
  },
  {
    name: "Cherry Blossom",
    colors: ["#5C001E", "#8B0032", "#C2185B", "#E91E63", "#F48FB1", "#FCE4EC"]
  },
  {
    name: "Deep Sea",
    colors: ["#001F3F", "#003366", "#00509E", "#0074D9", "#39CCCC", "#7FDBFF"]
  },
  {
    name: "Earth & Sky",
    colors: ["#3E2723", "#5D4037", "#795548", "#8D6E63", "#4FC3F7", "#B3E5FC"]
  },
  {
    name: "Desert Sands",
    colors: ["#5D4037", "#8D6E63", "#A1887F", "#D7CCC8", "#F5F5DC", "#FAF0E6"]
  },
  {
    name: "Midnight City",
    colors: ["#1A1A1D", "#4E4E50", "#6F2232", "#950740", "#C3073F", "#E5E5E5"]
  },
  {
    name: "Berry Smoothie",
    colors: ["#2C003E", "#512D6D", "#884EA0", "#C77DFF", "#E0AAFF", "#F3DDF9"]
  },
  {
    name: "Morning Fog",
    colors: ["#2F3542", "#57606F", "#747D8C", "#A4B0BE", "#CED6E0", "#F1F2F6"]
  },
  {
    name: "Tropical Paradise",
    colors: ["#004D40", "#00796B", "#009688", "#4DB6AC", "#80CBC4", "#B2DFDB"]
  },
  {
    name: "Coffee Break",
    colors: ["#3E2723", "#4E342E", "#6D4C41", "#8D6E63", "#A1887F", "#D7CCC8"]
  },
  {
    name: "Royal Velvet",
    colors: ["#311B92", "#4527A0", "#512DA8", "#673AB7", "#9575CD", "#D1C4E9"]
  },
  {
    name: "Golden Hour",
    colors: ["#F57F17", "#FBC02D", "#FDD835", "#FFEB3B", "#FFF176", "#FFF59D"]
  }
];

function Frame5({ onClose }: { onClose: () => void }) {
  return (
    <div className="relative shrink-0 size-[24px] cursor-pointer" data-name="Frame" onClick={onClose}>
      <svg className="block size-full" fill="none" preserveAspectRatio="none" viewBox="0 0 24 24">
        <g id="Frame">
          <path d="M18 6L6 18" id="Vector" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
          <path d="M6 6L18 18" id="Vector_2" stroke="var(--stroke-0, #344054)" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" />
        </g>
      </svg>
    </div>
  );
}

function Frame12({ onClose }: { onClose: () => void }) {
  return (
    <div className="content-stretch flex items-center justify-between relative shrink-0 w-full">
      <div className="flex flex-col font-sans font-bold justify-center leading-[0] relative shrink-0 text-[#101928] text-[20px] text-nowrap" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        <p className="leading-[normal]">Choose the colors that define your brand’s style.</p>
      </div>
      <Frame5 onClose={onClose} />
    </div>
  );
}

function Frame27() {
  return (
    <div className="content-stretch flex font-sans font-normal gap-[4px] items-center leading-[0] relative shrink-0 text-[13px] text-nowrap w-full">
      <div className="flex flex-col justify-center relative shrink-0 text-black" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        <p className="leading-[1.8] text-nowrap">🎨</p>
      </div>
      <div className="flex flex-col justify-center relative shrink-0 text-[#475367]" style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}>
        <p className="leading-[1.8] text-nowrap">Explore our ready-made color palette suggestions</p>
      </div>
    </div>
  );
}

function ColorBar({ colors }: { colors: string[] }) {
  return (
    <div className="content-stretch flex gap-[8px] h-[28px] items-center relative shrink-0 w-full">
      {colors.map((color, index) => (
        <div key={index} className="h-[28px] rounded-[4px] shrink-0 w-[56px]" style={{ backgroundColor: color }} />
      ))}
    </div>
  );
}

function PaletteItem({
  palette,
  isSelected,
  onClick,
}: {
  palette: ColorPalette;
  isSelected: boolean;
  onClick: () => void;
}) {
  return (
    <div
      onClick={onClick}
      className={`relative rounded-[4px] shrink-0 w-full cursor-pointer transition-colors ${isSelected
          ? "bg-gradient-to-b from-[rgba(11,162,155,0.05)] to-[rgba(67,180,141,0.05)]"
          : "hover:bg-gray-50"
        }`}
    >
      <div className="overflow-clip rounded-[inherit] size-full">
        <div className="content-stretch flex flex-col items-start p-[8px] relative w-full">
          <div className="content-stretch flex flex-col gap-[8px] items-start relative shrink-0 w-full">
            <div
              className="flex flex-col font-sans font-medium justify-center leading-[0] relative shrink-0 text-[#101928] text-[13px] w-full"
              style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
            >
              <p className="leading-[1.8]">{palette.name}</p>
            </div>
            <ColorBar colors={palette.colors} />
          </div>
        </div>
      </div>
      <div
        aria-hidden="true"
        className={`absolute border-[0.75px] border-solid inset-0 pointer-events-none rounded-[4px] ${isSelected ? "border-[#0ba29b]" : "border-[#e4e7ec]"
          }`}
      />
    </div>
  );
}

function Frame28({
  selectedPaletteNames,
  onTogglePalette,
}: {
  selectedPaletteNames: string[];
  onTogglePalette: (name: string) => void;
}) {
  return (
    <div className="content-stretch flex flex-col gap-[8px] h-[340px] items-start overflow-y-auto relative shrink-0 w-full pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-thumb]:bg-gray-300 [&::-webkit-scrollbar-thumb]:rounded-full [&::-webkit-scrollbar-track]:bg-transparent">
      {colorPalettes.map((palette) => (
        <PaletteItem
          key={palette.name}
          palette={palette}
          isSelected={selectedPaletteNames.includes(palette.name)}
          onClick={() => onTogglePalette(palette.name)}
        />
      ))}
    </div>
  );
}

function Frame29({
  selectedPaletteNames,
  onTogglePalette,
}: {
  selectedPaletteNames: string[];
  onTogglePalette: (name: string) => void;
}) {
  return (
    <div className="content-stretch flex flex-col gap-[12px] items-start relative shrink-0 w-full">
      <Frame27 />
      <Frame28
        selectedPaletteNames={selectedPaletteNames}
        onTogglePalette={onTogglePalette}
      />
    </div>
  );
}

function Frame95({
  onClose,
  selectedPaletteNames,
  onTogglePalette,
}: {
  onClose: () => void;
  selectedPaletteNames: string[];
  onTogglePalette: (name: string) => void;
}) {
  return (
    <div className="content-stretch flex flex-col gap-[24px] items-start relative shrink-0 w-full">
      <Frame12 onClose={onClose} />
      <Frame29
        selectedPaletteNames={selectedPaletteNames}
        onTogglePalette={onTogglePalette}
      />
    </div>
  );
}

function PrimarySm5({ isEnabled, onClick }: { isEnabled: boolean; onClick?: () => void }) {
  return (
    <div
      onClick={isEnabled ? onClick : undefined}
      className={`${isEnabled ? "bg-[#30A84B] cursor-pointer" : "bg-[#bfe4c7] cursor-not-allowed"
        } content-stretch flex gap-[4px] h-[36px] items-center justify-center p-[12px] relative rounded-[6px] shrink-0 transition-colors`}
      data-name="Primary - Sm"
    >
      <p
        className="font-sans font-medium leading-[normal] relative shrink-0 text-[14px] text-nowrap text-white"
        style={{ fontVariationSettings: "'opsz' 14, 'wdth' 100" }}
      >
        Add Colors
      </p>
    </div>
  );
}

function Frame11({ onClose, onAddColors }: { onClose: () => void; onAddColors?: (palettes: ColorPalette[]) => void }) {
  const [selectedPaletteNames, setSelectedPaletteNames] = React.useState<string[]>([]);

  const handleTogglePalette = (name: string) => {
    setSelectedPaletteNames(prev =>
      prev.includes(name)
        ? prev.filter(p => p !== name)
        : [...prev, name]
    );
  };

  const handleAddColors = () => {
    if (selectedPaletteNames.length > 0 && onAddColors) {
      const selected = colorPalettes.filter(p => selectedPaletteNames.includes(p.name));
      onAddColors(selected);
    }
  };

  return (
    <div
      className="absolute bg-white content-stretch flex flex-col gap-[32px] items-end overflow-clip px-[18px] py-[23px] rounded-[12px] w-[672px] shadow-xl left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
      onClick={(e) => e.stopPropagation()}
    >
      <Frame95
        onClose={onClose}
        selectedPaletteNames={selectedPaletteNames}
        onTogglePalette={handleTogglePalette}
      />
      <PrimarySm5 isEnabled={selectedPaletteNames.length > 0} onClick={handleAddColors} />
    </div>
  );
}

export default function ColorSelectionModal({ isOpen, onClose, onAddColors }: { isOpen: boolean; onClose: () => void; onAddColors?: (palettes: ColorPalette[]) => void }) {
  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed z-[100] inset-0 backdrop-blur-[2.5px] backdrop-filter bg-[rgba(16,25,40,0.5)] h-full w-full"
          onClick={onClose}
        >
          <Frame11 onClose={onClose} onAddColors={onAddColors} />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
